import React, { memo, useMemo, useCallback } from "react";
import {
  Box,
  VStack,
  Text,
  Icon,
  Flex,
  Button,
  HStack,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  Table,
  Checkbox,
} from "@chakra-ui/react";
import { MoreVertical } from "lucide-react";
import Card from "@/components/ui/Card";
import TableText from "@/components/ui/TableText";
import { tableHeaderColumnStyles, actionsHeaderColumnStyles } from "./styles";
import type { ItemId } from "@/hooks/useMultiSelect";

export interface TableColumn<T> {
  key: string;
  title: string;
  align?: "left" | "right";
  render?: (item: T) => React.ReactNode;
  variant?: "id" | "primary" | "default";
}

export interface TableAction<T> {
  key: string;
  label: string;
  icon: React.ComponentType;
  onClick: (item: T) => void;
  disabled?: boolean;
  colorPalette?: string;
  visible?: (item: T) => boolean; // Function to determine if action should be visible for this item
}

// Generic action handler type for better type safety across all tables
export type ActionHandler<T, A extends string> = (action: A, entity: T) => void;

export interface BulkActionMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  colorPalette?: string;
}

export interface BulkAction {
  key: string;
  label: string;
  icon: React.ComponentType;
  items: BulkActionMenuItem[];
  disabled?: boolean;
}

export interface MultiSelectConfig<T> {
  selectedIds: Set<ItemId>;
  onToggle: (id: ItemId) => void;
  onToggleAll: (allIds: ItemId[]) => void;
  isAllSelected: boolean;
  getItemId: (item: T) => ItemId;
}

export interface EmptyState {
  icon: React.ComponentType;
  title: string;
  description: string;
}

interface EntityTableSectionProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  keyExtractor?: (item: T) => string | number;
  isLoading?: boolean;
  isPollingEnabled?: boolean;
  pollingError?: any;
  showPlaceholderData?: boolean;
  emptyState: EmptyState;
  onRowClick?: (item: T) => void;
  multiSelect?: MultiSelectConfig<T>;
}

const checkboxColumnStyles = {
  width: "40px",
  px: 2,
};

// Memoized row component using standard shallow comparison
const TableRowComponent = memo(function TableRowComponent<T>({
  item,
  columns,
  actions = [],
  onRowClick,
  isSelected = false,
  onToggle,
  itemId,
}: {
  item: T;
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  onRowClick?: (item: T) => void;
  isSelected?: boolean;
  onToggle?: (id: ItemId) => void;
  itemId?: ItemId;
}) {
  // Memoize visible actions filtering
  const visibleActions = useMemo(() => 
    actions.filter((action) =>
      action.visible ? action.visible(item) : true
    ),
    [actions, item]
  );

  const handleRowClick = useCallback((event: React.MouseEvent) => {
    // Don't trigger row click if clicking on a link, button, menu, or action
    const target = event.target as HTMLElement;
    const isClickableElement = target.closest('a, button, [role="menuitem"], [data-state], input[type="checkbox"], label');
    
    if (!isClickableElement && onRowClick) {
      onRowClick(item);
    }
  }, [onRowClick, item]);
  
  const handleToggle = useCallback(() => {
    if (onToggle && itemId !== undefined) {
      onToggle(itemId);
    }
  }, [onToggle, itemId]);

  return (
    <Table.Row 
      _last={{ borderBottom: "none" }}
      cursor={onRowClick ? "pointer" : "default"}
      _hover={onRowClick ? { bg: "gray.50", _dark: { bg: "gray.800" } } : undefined}
      onClick={handleRowClick}
      bg={isSelected ? { base: "brand.50", _dark: "brand.950" } : undefined}
    >
      {onToggle && itemId !== undefined && (
        <Table.Cell {...checkboxColumnStyles}>
          <Checkbox.Root
            size="sm"
            checked={isSelected}
            onCheckedChange={handleToggle}
            colorPalette="brand"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        </Table.Cell>
      )}
      {columns.map((column) => (
        <Table.Cell
          key={column.key}
          textAlign={column.align || "left"}
          whiteSpace={column.variant === "id" ? "nowrap" : undefined}
        >
          {column.render ? (
            column.render(item)
          ) : (
            <TableText variant={column.variant}>
              {(item as any)[column.key]}
            </TableText>
          )}
        </Table.Cell>
      ))}
      {visibleActions.length > 0 && (
        <Table.Cell textAlign="center">
          <Box position="relative">
            <MenuRoot>
              <MenuTrigger asChild>
                <Button
                  size="xs"
                  variant="ghost"
                  p={1}
                  title="Actions"
                >
                  <Icon as={MoreVertical} boxSize={3.5} />
                </Button>
              </MenuTrigger>
              <MenuContent
                position="absolute"
                top="100%"
                right={0}
                zIndex={1000}
                bg="white"
                borderColor="gray.200"
                borderWidth={1}
                borderRadius="md"
                boxShadow="lg"
                minW="200px"
                maxW="280px"
                _dark={{
                  bg: "gray.700",
                  borderColor: "gray.600",
                }}
              >
                {visibleActions.map((action) => (
                  <MenuItem
                    key={action.key}
                    value={action.key}
                    onClick={() => action.onClick(item)}
                    disabled={action.disabled}
                    color={
                      action.colorPalette === "red"
                        ? "red.500"
                        : undefined
                    }
                    px={3}
                    py={2}
                    _hover={{
                      bg:
                        action.colorPalette === "red"
                          ? "red.50"
                          : "gray.100",
                      _dark: {
                        bg:
                          action.colorPalette === "red"
                            ? "red.900"
                            : "gray.700",
                      },
                    }}
                  >
                    <HStack gap={2} whiteSpace="nowrap">
                      <Icon as={action.icon} boxSize={4} />
                      <Text fontSize="sm">{action.label}</Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuContent>
            </MenuRoot>
          </Box>
        </Table.Cell>
      )}
    </Table.Row>
  );
}) as <T>(props: {
  item: T;
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  onRowClick?: (item: T) => void;
  isSelected?: boolean;
  onToggle?: (id: ItemId) => void;
  itemId?: ItemId;
}) => React.ReactElement;

function EntityTableSection<T>({
  data,
  columns,
  actions = [],
  keyExtractor,
  isLoading = false,
  isPollingEnabled = false,
  pollingError = null,
  showPlaceholderData = false,
  emptyState,
  onRowClick,
  multiSelect,
}: EntityTableSectionProps<T>) {
  // Show loading state when loading with no data
  if (isLoading && data.length === 0) {
    return (
      <Card>
        <Flex justify="center" align="center" py={12}>
          <VStack gap={2}>
            <Text color="gray.500">Loading...</Text>
            <Text color="gray.400" fontSize="sm">Please wait while we fetch the data</Text>
          </VStack>
        </Flex>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <Flex justify="center" align="center" py={12}>
          <VStack gap={3}>
            <Icon as={emptyState.icon} boxSize={12} color="gray.400" />
            <Text color="gray.500" fontSize="lg" fontWeight="medium">
              {emptyState.title}
            </Text>
            <Text color="gray.400" fontSize="sm" textAlign="center">
              {emptyState.description}
            </Text>
          </VStack>
        </Flex>
      </Card>
    );
  }

  // Compute allIds lazily on click - more efficient than computing on every render
  // Use ref to access latest multiSelect without causing re-renders
  const multiSelectRef = React.useRef(multiSelect);
  React.useEffect(() => {
    multiSelectRef.current = multiSelect;
  }, [multiSelect]);
  
  const handleToggleAll = useCallback(() => {
    const currentMultiSelect = multiSelectRef.current;
    if (!currentMultiSelect) return;
    const allIds = data.map((item) => currentMultiSelect.getItemId(item));
    currentMultiSelect.onToggleAll(allIds);
  }, [data]);

  return (
    <Card position="relative">
      {/* Show loading overlay when loading but data exists (e.g., during search/filter) */}
      {isLoading && data.length > 0 && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.700"
          _dark={{ bg: "blackAlpha.700" }}
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="md"
        >
          <Text color="gray.500" fontSize="sm">Loading...</Text>
        </Box>
      )}
      <Table.Root
        size="sm"
        interactive
        css={{
          "& tbody tr:last-child td": {
            borderBottomWidth: 0,
          },
          ...(isLoading && data.length > 0 ? { opacity: 0.6 } : {}),
        }}
      >
        <Table.Header>
          <Table.Row>
            {multiSelect && (
              <Table.ColumnHeader {...checkboxColumnStyles}>
                <Checkbox.Root
                  size="sm"
                  checked={multiSelect.isAllSelected}
                  onCheckedChange={handleToggleAll}
                  colorPalette="brand"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                </Checkbox.Root>
              </Table.ColumnHeader>
            )}
            {columns.map((column) => (
              <Table.ColumnHeader
                key={column.key}
                textAlign={column.align || "left"}
                {...tableHeaderColumnStyles}
              >
                {column.title}
              </Table.ColumnHeader>
            ))}
            {actions.length > 0 && (
              <Table.ColumnHeader
                key="actions"
                {...actionsHeaderColumnStyles}
              >
                ACTIONS
              </Table.ColumnHeader>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item, index) => {
            const itemId = multiSelect?.getItemId(item);
            const isSelected = itemId !== undefined ? multiSelect?.selectedIds.has(itemId) : false;
            
            return (
              <TableRowComponent
                key={
                  keyExtractor ? keyExtractor(item) : (item as any).id || index
                }
                item={item}
                columns={columns}
                actions={actions}
                onRowClick={onRowClick}
                isSelected={isSelected}
                itemId={itemId}
                onToggle={multiSelect?.onToggle}
              />
            );
          })}
        </Table.Body>
      </Table.Root>
    </Card>
  );
}

// Export the memoized component
export default memo(EntityTableSection) as typeof EntityTableSection;
